// =============================================================================
// HUB COMMERCIAL - Gestion des Prospects / Clients
// Migré vers OHGrid — GROOT: UI-005, UI-006, UI-007, UI-002, UI-004
// =============================================================================
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Users, Plus, User, Phone, Mail, MapPin, Building2, Building,
  Eye, FolderPlus, RefreshCw, X, Check, Archive, ArchiveRestore,
  Trash2, AlertTriangle, Loader2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { OHGrid } from "../../components/ui/oh-grid";
import { formatClientFullName } from "../../utils/clientFormat";
import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";
import { AdresseAutocomplete } from "../../components/metier/AdresseAutocomplete";
import { normalizeName } from "../../lib/normalize";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProspectsList({ tenantId, onNavigate, onCreateDossier }) {
  const { items: civiliteOptions } = useConfigList("civilites");
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [archiveDialog, setArchiveDialog] = useState({ open: false, client: null, action: null });
  const [actionLoading, setActionLoading] = useState(false);
  // CC-41 V1 — Doublon client détecté backend (409)
  const [dupDialog, setDupDialog] = useState({ open: false, matches: [] });

  // Form state
  const [formData, setFormData] = useState({
    civilite: "", nom: "", prenom: "", email: "", telephone: "",
    type_client: "particulier", raison_sociale: "", siren: "", siret: "",
    forme_juridique: "", representant_nom: "", representant_fonction: "",
    adresse: { rue: "", code_postal: "", ville: "" }, rfr: "", nombre_parts: "1"
  });
  const [searchingSiren, setSearchingSiren] = useState(false);
  const [sirenResults, setSirenResults] = useState([]);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }, [tenantId]);

  useEffect(() => { loadData(); }, [tenantId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [tenantRes, clientsRes] = await Promise.all([
        fetch(`${API}/api/metier/tenant`, { headers, credentials: "include" }),
        fetch(`${API}/api/metier/clients?limit=500`, { headers, credentials: "include" }),
      ]);
      if (tenantRes.ok) setTenantInfo(await tenantRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (err) { console.error("Erreur chargement:", err); }
    finally { setLoading(false); }
  };

  // ─── Filtrage (via advancedFilters callbacks) ───
  const filteredClients = useMemo(() => clients.filter(c => {
    const matchType = filterType === "all" || c.type_client === filterType;
    const isArchived = c.archived === true || c.statut === "archive";
    const matchArchived = showArchived || !isArchived;
    return matchType && matchArchived;
  }), [clients, filterType, showArchived]);

  // ─── Enrichment pour colonnes ───
  const enrichedData = useMemo(() => filteredClients.map(c => ({
    ...c,
    // P0-04 fix BUG-11 (2026-05-03) — formatClientFullName gere aussi raison_sociale
    display_name: formatClientFullName(c),
    display_contact_phone: c.telephone || "",
    display_contact_email: c.email || "",
    display_ville: `${c.adresse?.code_postal || ""} ${c.adresse?.ville || ""}`.trim() || "—",
    display_type: c.type_client || "particulier",
    display_statut: (c.archived === true || c.statut === "archive") ? "archive" : "actif",
  })), [filteredClients]);

  // ─── Stats CLIQUABLES ───
  const allClients = useMemo(() => clients.filter(c => {
    const isArchived = c.archived === true || c.statut === "archive";
    return showArchived || !isArchived;
  }), [clients, showArchived]);

  const stats = useMemo(() => [
    { key: "total", label: "Total", value: allClients.length, icon: Users, color: "purple",
      filter: null }, // pas de filtre = montre tout
    { key: "particuliers", label: "Particuliers", value: allClients.filter(c => c.type_client === "particulier" || !c.type_client).length, icon: User, color: "blue",
      filter: (row) => row.type_client === "particulier" || !row.type_client },
    { key: "with_email", label: "Avec email", value: allClients.filter(c => c.email).length, icon: Mail, color: "green",
      filter: (row) => !!row.email },
    { key: "with_phone", label: "Avec tél.", value: allClients.filter(c => c.telephone).length, icon: Phone, color: "cyan",
      filter: (row) => !!row.telephone },
  ], [allClients]);

  // ─── Filtres avancés ───
  const advancedFilters = useMemo(() => [
    { key: "type_client", label: "Type", type: "select", defaultValue: "all",
      options: [
        { value: "particulier", label: "Particulier" },
        { value: "professionnel", label: "Professionnel" },
        { value: "copropriete", label: "Copropriété" },
        { value: "bailleur", label: "Bailleur" },
      ]},
    { key: "archived", label: "Inclure archivés", type: "checkbox", defaultValue: false },
  ], []);

  const handleAdvFilter = useCallback(({ key, value }) => {
    if (key === "type_client") setFilterType(value);
    if (key === "archived") setShowArchived(value);
  }, []);

  // ─── Columns ───
  const columns = useMemo(() => [
    { key: "reference", label: "Réf", mono: true, sortable: true, render: (v) => (
      <span
        className="text-xs font-mono"
        style={{ color: v ? "var(--oh-text)" : "var(--oh-text-muted)" }}
        data-testid="client-grid-reference"
      >
        {v || "Sans réf."}
      </span>
    )},
    { key: "display_name", label: "Nom", render: (v, row) => (
      <div className="flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-[var(--oh-bg-elevated)] flex items-center justify-center shrink-0">
          <User className="w-3.5 h-3.5 text-[var(--oh-text-muted)]" />
        </div>
        <span className="font-medium">{v || "—"}</span>
      </div>
    )},
    { key: "display_contact_phone", label: "Contact", sortable: false, render: (v, row) => (
      <div className="space-y-0.5">
        {row.telephone && <div className="flex items-center gap-1.5 text-xs" style={{ color: "var(--oh-text-muted)" }}><Phone className="w-3 h-3" />{row.telephone}</div>}
        {row.email && <div className="flex items-center gap-1.5 text-xs truncate max-w-[200px]" style={{ color: "var(--oh-text-muted)" }}><Mail className="w-3 h-3" />{row.email}</div>}
      </div>
    )},
    { key: "display_ville", label: "Ville", render: (v) => <span className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{v}</span> },
    { key: "display_type", label: "Type", render: (v) => <Badge variant="outline" className="text-xs">{v}</Badge> },
    { key: "display_statut", label: "Statut", render: (v) => v === "archive"
      ? <Badge className="bg-[var(--oh-accent-bg)] text-[var(--oh-accent-text)] text-[11px]">Archivé</Badge>
      : <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-[11px]">Actif</Badge>
    },
  ], []);

  // ─── Row Actions ───
  const rowActions = useMemo(() => [
    { label: "Voir détails", icon: Eye, onClick: (row) => onNavigate?.("client", { id: row.id }) },
    { label: "Créer un dossier", icon: FolderPlus, onClick: (row) => onCreateDossier?.(row.id),
      show: (row) => !(row.archived === true || row.statut === "archive") },
    { label: "Archiver", icon: Archive, onClick: (row) => setArchiveDialog({ open: true, client: row, action: "archive" }),
      show: (row) => !(row.archived === true || row.statut === "archive"), className: "text-[var(--oh-info-text)]" },
    { label: "Désarchiver", icon: ArchiveRestore, onClick: (row) => setArchiveDialog({ open: true, client: row, action: "unarchive" }),
      show: (row) => row.archived === true || row.statut === "archive", className: "text-[var(--oh-accent-text)]" },
    { label: "Supprimer", icon: Trash2, onClick: (row) => setArchiveDialog({ open: true, client: row, action: "delete" }),
      className: "text-[var(--oh-error-text)]" },
  ], [onNavigate, onCreateDossier]);

  // ─── Bulk Actions ───
  const executeBulk = async (action, ids) => {
    const label = action === "archive" ? "Archiver" : action === "unarchive" ? "Désarchiver" : "Supprimer";
    if (!window.confirm(`${label} ${ids.length} client(s) ?`)) return;
    const headers = getHeaders();
    let ok = 0, errors = 0;
    for (const id of ids) {
      try {
        const endpoint = action === "delete"
          ? `${API}/api/metier/clients/${id}`
          : `${API}/api/metier/clients/${id}/${action === "archive" ? "archiver" : "desarchiver"}`;
        const method = action === "delete" ? "DELETE" : "POST";
        const res = await fetch(endpoint, { method, headers, credentials: "include" });
        if (res.ok) ok++; else errors++;
      } catch { errors++; }
    }
    if (ok > 0) { toast.success(`${ok} client(s) traité(s)`); loadData(); }
    if (errors > 0) toast.error(`${errors} erreur(s)`);
  };

  const bulkActions = useMemo(() => [
    { label: "Archiver", icon: Archive, onClick: (ids) => executeBulk("archive", ids), className: "border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]" },
    { label: "Supprimer", icon: Trash2, onClick: (ids) => executeBulk("delete", ids), className: "border-[var(--oh-error-border)] text-[var(--oh-error-text)] hover:bg-[var(--oh-error-bg)]" },
  ], []);

  // ─── Search Filter ───
  const searchFilter = useCallback((row, q) => (
    (row.nom || "").toLowerCase().includes(q) ||
    (row.prenom || "").toLowerCase().includes(q) ||
    (row.raison_sociale || "").toLowerCase().includes(q) ||
    (row.email || "").toLowerCase().includes(q) ||
    (row.telephone || "").includes(q) ||
    (row.display_name || "").toLowerCase().includes(q)
  ), []);

  // ─── Action dialog handler ───
  const handleArchiveAction = async () => {
    if (!archiveDialog.client || !archiveDialog.action) return;
    setActionLoading(true);
    try {
      const action = archiveDialog.action;
      const clientId = archiveDialog.client.id;
      const endpoint = action === "delete"
        ? `${API}/api/metier/clients/${clientId}`
        : `${API}/api/metier/clients/${clientId}/${action === "archive" ? "archiver" : "desarchiver"}`;
      const method = action === "delete" ? "DELETE" : "POST";
      const res = await fetch(endpoint, { method, headers: getHeaders(), credentials: "include" });
      if (res.ok) {
        toast.success(action === "delete" ? "Client supprimé" : action === "archive" ? "Client archivé" : "Client désarchivé");
        loadData();
        setArchiveDialog({ open: false, client: null, action: null });
      } else {
        const error = await res.json().catch(() => ({}));
        toast.error(error.detail || "Erreur");
      }
    } catch { toast.error("Erreur réseau"); }
    finally { setActionLoading(false); }
  };

  // ─── Create client ───
  const handleCreateClient = async (forceCreate = false) => {
    setCreating(true);
    try {
      const payload = {
        ...formData,
        rfr: formData.rfr ? parseFloat(formData.rfr) : null,
        nombre_parts: formData.nombre_parts ? parseFloat(formData.nombre_parts) : null,
      };
      const url = `${API}/api/metier/clients${forceCreate ? "?force=true" : ""}`;
      const res = await fetch(url, {
        method: "POST", headers: getHeaders(), credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const newClient = await res.json();
        setClients([newClient, ...clients]);
        setShowCreateModal(false);
        resetForm();
        toast.success("Client créé");
      } else if (res.status === 409) {
        // CC-41 V1 — fuzzy doublon détecté backend
        const err = await res.json().catch(() => ({}));
        const candidates = err?.detail?.candidates || [];
        setDupDialog({
          open: true,
          matches: candidates.map((c) => ({
            id: c.client_id, nom: c.nom, prenom: c.prenom,
            email: c.email || "", telephone: "",
            adresse: { code_postal: c.code_postal, ville: c.ville },
            reference: c.reference, anah_reference: c.anah_reference,
            score: c.score, match_reasons: c.match_reasons || [],
            dossiers_actifs_count: 0,
          })),
        });
      } else {
        toast.error(`Erreur création (${res.status})`);
      }
    } catch { toast.error("Erreur création"); }
    finally { setCreating(false); }
  };

  const resetForm = () => {
    setFormData({
      civilite: "", nom: "", prenom: "", email: "", telephone: "",
      type_client: "particulier", raison_sociale: "", siren: "", siret: "",
      forme_juridique: "", representant_nom: "", representant_fonction: "",
      adresse: { rue: "", code_postal: "", ville: "" }, rfr: "", nombre_parts: "1",
    });
    setSirenResults([]);
  };

  const searchEntreprise = async (query) => {
    if (!query || query.length < 3) { setSirenResults([]); return; }
    setSearchingSiren(true);
    try {
      const res = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(query)}&per_page=5`);
      if (res.ok) setSirenResults((await res.json()).results || []);
    } catch {} finally { setSearchingSiren(false); }
  };

  const selectEntreprise = (entreprise) => {
    const siege = entreprise.siege || {};
    setFormData({
      ...formData,
      raison_sociale: entreprise.nom_complet || entreprise.nom_raison_sociale || "",
      siren: entreprise.siren || "", siret: siege.siret || "",
      forme_juridique: entreprise.nature_juridique || "",
      adresse: { rue: siege.adresse || "", code_postal: siege.code_postal || "", ville: siege.libelle_commune || "" },
    });
    setSirenResults([]);
  };

  const isB2B = ["entreprise", "professionnel", "copropriete", "bailleur"].includes(formData.type_client);
  const hasTEF = tenantInfo?.activites_disponibles?.tef;

  return (
    <div className="space-y-4" data-testid="prospects-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[var(--oh-accent-bg)]">
            <Users className="w-5 h-5 text-[var(--oh-accent-text)]" />
          </div>
          <div>
            <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Prospects & Clients</h2>
            <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>{filteredClients.length} contacts</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]" data-testid="btn-new-prospect">
          <Plus className="w-4 h-4 mr-2" /> Nouveau prospect
        </Button>
      </div>

      {/* OHGrid avec stats cliquables + filtres avancés */}
      <OHGrid
        data={enrichedData}
        columns={columns}
        stats={stats}
        advancedFilters={advancedFilters}
        onAdvancedFilter={handleAdvFilter}
        rowActions={rowActions}
        bulkActions={bulkActions}
        selectable={true}
        pagination={true}
        pageSize={50}
        loading={loading}
        onRefresh={loadData}
        onRowClick={(row) => onNavigate?.("client", { id: row.id })}
        searchPlaceholder="Rechercher un nom, email, téléphone..."
        searchFilter={searchFilter}
        defaultSortKey="nom"
        defaultSortDir="asc"
        emptyMessage="Aucun prospect trouvé"
        testIdPrefix="prospects"
      />

      {/* Modal Création */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg" style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}>
          <DialogHeader>
            <DialogTitle style={{ color: "var(--oh-text)" }}>Nouveau Prospect</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <Select value={formData.civilite} onValueChange={(v) => setFormData({ ...formData, civilite: v })}>
                <SelectTrigger style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}>
                  <SelectValue placeholder="Civilité" />
                </SelectTrigger>
                <SelectContent>
                  {civiliteOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder="Nom *" value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                onBlur={(e) => setFormData({ ...formData, nom: normalizeName(e.target.value, "nom") })}
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              <Input placeholder="Prénom" value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                onBlur={(e) => setFormData({ ...formData, prenom: normalizeName(e.target.value, "prenom") })}
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input placeholder="Email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              <Input placeholder="Téléphone" value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
            </div>
            <Select value={formData.type_client} onValueChange={(v) => setFormData({ ...formData, type_client: v })}>
              <SelectTrigger style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }}>
                <SelectValue placeholder="Type de client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="particulier">Particulier</SelectItem>
                <SelectItem value="entreprise">Entreprise</SelectItem>
                <SelectItem value="professionnel">Professionnel</SelectItem>
                <SelectItem value="copropriete">Copropriété</SelectItem>
                <SelectItem value="bailleur">Bailleur</SelectItem>
              </SelectContent>
            </Select>

            {/* B2B — Recherche SIREN */}
            {isB2B && (
              <div className="space-y-4 p-4 rounded-lg" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                <p className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--oh-text-muted)" }}>
                  <Building className="w-4 h-4" /> Informations entreprise
                </p>
                <div className="relative">
                  <Input placeholder="Rechercher par nom ou SIREN..." value={formData.raison_sociale}
                    onChange={(e) => { setFormData({ ...formData, raison_sociale: e.target.value }); searchEntreprise(e.target.value); }}
                    onBlur={(e) => setFormData({ ...formData, raison_sociale: normalizeName(e.target.value, "entreprise") })}
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                  {searchingSiren && <div className="absolute right-3 top-1/2 -translate-y-1/2"><Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>}
                  {sirenResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
                      {sirenResults.map((ent) => (
                        <button key={ent.siren} type="button" onClick={() => selectEntreprise(ent)}
                          className="w-full p-3 text-left hover:opacity-80" style={{ borderBottom: "1px solid var(--oh-border)" }}>
                          <p className="font-medium" style={{ color: "var(--oh-text)" }}>{ent.nom_complet || ent.nom_raison_sociale}</p>
                          <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>SIREN: {ent.siren} — {ent.siege?.libelle_commune || ""}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input placeholder="SIREN" value={formData.siren} maxLength={9} onChange={(e) => setFormData({ ...formData, siren: e.target.value })}
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                  <Input placeholder="SIRET" value={formData.siret} maxLength={14} onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                </div>
                <Input placeholder="Forme juridique (SAS, SARL, etc.)" value={formData.forme_juridique} onChange={(e) => setFormData({ ...formData, forme_juridique: e.target.value })}
                  style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input placeholder="Nom du représentant" value={formData.representant_nom}
                    onChange={(e) => setFormData({ ...formData, representant_nom: e.target.value })}
                    onBlur={(e) => setFormData({ ...formData, representant_nom: normalizeName(e.target.value, "nom") })}
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                  <Input placeholder="Fonction (Gérant, PDG...)" value={formData.representant_fonction} onChange={(e) => setFormData({ ...formData, representant_fonction: e.target.value })}
                    style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                </div>
              </div>
            )}

            {/* Adresse */}
            <div className="space-y-2">
              <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Adresse</p>
              <AdresseAutocomplete
                placeholder="Rechercher une adresse (BAN)..."
                onSelect={(addr) => setFormData({
                  ...formData,
                  adresse: {
                    rue: [addr.numero, addr.rue].filter(Boolean).join(" ").trim(),
                    code_postal: addr.code_postal || "",
                    ville: addr.ville || "",
                  },
                })}
              />
              <Input placeholder="Rue" value={formData.adresse.rue}
                onChange={(e) => setFormData({ ...formData, adresse: { ...formData.adresse, rue: e.target.value } })}
                onBlur={(e) => setFormData({ ...formData, adresse: { ...formData.adresse, rue: normalizeName(e.target.value, "adresse") } })}
                style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input placeholder="Code postal" value={formData.adresse.code_postal} onChange={(e) => setFormData({ ...formData, adresse: { ...formData.adresse, code_postal: e.target.value } })}
                  style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                <Input placeholder="Ville" value={formData.adresse.ville}
                  onChange={(e) => setFormData({ ...formData, adresse: { ...formData.adresse, ville: e.target.value } })}
                  onBlur={(e) => setFormData({ ...formData, adresse: { ...formData.adresse, ville: normalizeName(e.target.value, "ville") } })}
                  style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
              </div>
            </div>

            {/* TEF Fiscal */}
            {hasTEF && (
              <div className="space-y-2 pt-2" style={{ borderTop: "1px solid var(--oh-border)" }}>
                <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>Informations fiscales (TEF)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input placeholder="RFR (€)" type="number" value={formData.rfr} onChange={(e) => setFormData({ ...formData, rfr: e.target.value })}
                    style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                  <Input placeholder="Nb parts fiscales" type="number" step="0.5" value={formData.nombre_parts} onChange={(e) => setFormData({ ...formData, nombre_parts: e.target.value })}
                    style={{ background: "var(--oh-bg-elevated)", borderColor: "var(--oh-border)", color: "var(--oh-text)" }} />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Annuler</Button>
              <Button onClick={handleCreateClient} disabled={!formData.nom || creating} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]" data-testid="btn-create-client">
                {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Créer</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Archiver/Supprimer */}
      <Dialog open={archiveDialog.open} onOpenChange={(open) => !open && setArchiveDialog({ open: false, client: null, action: null })}>
        <DialogContent style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}>
          <DialogHeader>
            <DialogTitle style={{ color: "var(--oh-text)" }} className="flex items-center gap-2">
              {archiveDialog.action === "delete" && <><AlertTriangle className="w-5 h-5 text-[var(--oh-error-text)]" /> Supprimer le client</>}
              {archiveDialog.action === "archive" && <><Archive className="w-5 h-5 text-[var(--oh-info-text)]" /> Archiver le client</>}
              {archiveDialog.action === "unarchive" && <><ArchiveRestore className="w-5 h-5 text-[var(--oh-accent-text)]" /> Désarchiver le client</>}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3">
            <p style={{ color: "var(--oh-text-muted)" }}>
              Confirmer pour <strong style={{ color: "var(--oh-text)" }}>{formatClientFullName(archiveDialog.client)}</strong> ?
            </p>
            {archiveDialog.action === "delete" && (
              <div className="mt-3 p-3 rounded-lg bg-[var(--oh-error-bg)] border border-[var(--oh-error-border)]">
                <p className="text-[var(--oh-error-text)] text-sm"><AlertTriangle className="w-4 h-4 inline mr-1" /> Action irréversible. Le client ne peut être supprimé que s'il n'a aucun dossier associé.</p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setArchiveDialog({ open: false, client: null, action: null })} disabled={actionLoading}>Annuler</Button>
            <Button onClick={handleArchiveAction} disabled={actionLoading}
              className={archiveDialog.action === "delete" ? "bg-red-600 hover:bg-red-700" : "bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]"}
              data-testid="confirm-client-action-btn">
              {actionLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : archiveDialog.action === "delete" ? "Supprimer" : archiveDialog.action === "archive" ? "Archiver" : "Désarchiver"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ClientDuplicateDialog
        open={dupDialog.open}
        mode="client"
        matches={dupDialog.matches}
        onCancel={() => setDupDialog({ open: false, matches: [] })}
        onUseExisting={(m) => {
          setDupDialog({ open: false, matches: [] });
          setShowCreateModal(false);
          resetForm();
          if (onNavigate) onNavigate("client-detail", { clientId: m.id });
          toast.info(`Client existant : ${m.prenom || ""} ${m.nom || ""}`);
        }}
        onCreateAnyway={() => {
          setDupDialog({ open: false, matches: [] });
          handleCreateClient(true);
        }}
      />
    </div>
  );
}
